'use client';

import { Box, Button, Heading } from '@skin-seoul/design-system';
import LucideIcon from '@/components/ui/LucideIcon';
import { ImpressionArea, useIsTouchDevice } from '@skin-seoul/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import type { TrendingVideo } from '@skin-seoul/service';
import { noop } from 'es-toolkit';
import { useVideoInitialization } from './hooks/useVideoInitialization';
type VideoCardProps = TrendingVideo & {
  onClick?: () => void;
  activeVideoId: string | null;
  onUnmute: (videoId: string) => void;
};
const VideoCard = ({
  id,
  videoUrl,
  productFocus,
  onClick,
  activeVideoId,
  onUnmute
}: VideoCardProps) => {
  const isSafari = typeof navigator !== 'undefined' && /safari/i.test(navigator.userAgent) && !/chrome|crios|android/i.test(navigator.userAgent);
  const IMPRESSION_AREA_THRESHOLD = 0.6;
  const IMPRESSION_TIME_THRESHOLD_MS = 200;
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoParentRef = useRef<HTMLDivElement | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const isTouchDevice = useIsTouchDevice();
  useVideoInitialization(videoEl, isMuted);

  // Attach the actual video element created via innerHTML
  useEffect(() => {
    const parent = videoParentRef.current;
    if (!parent) {
      return;
    }
    const child = parent.children[0] as HTMLVideoElement | undefined;
    setVideoEl(child ?? null);
  }, [videoUrl]);

  // Safari-specific bootstrap: enforce attributes early and try immediate play once (per shared snippet)
  useEffect(() => {
    if (!videoEl || !isSafari) {
      return;
    }
    const parent = videoParentRef.current;
    if (!parent) {
      return;
    }
    const player = parent.children[0] as HTMLVideoElement | undefined;
    if (!player) {
      return;
    }
    player.controls = false;
    player.playsInline = true;
    player.muted = true;
    player.setAttribute('muted', '');
    player.autoplay = true;
    // Attempt immediate play on next tick
    const t = setTimeout(() => {
      const promise = player.play() as unknown;
      if (promise && typeof (promise as Promise<void>).then === 'function') {
        (promise as Promise<void>).then(noop).catch(() => {
          // Fallback: hide video container (per shared snippet)
          if (videoParentRef.current) {
            videoParentRef.current.style.display = 'none';
          }
        });
      }
    }, 0);
    return () => clearTimeout(t);
  }, [isSafari, videoEl]);
  useEffect(() => {
    if (activeVideoId && activeVideoId !== id) {
      setIsMuted(true);
      if (videoEl) {
        videoEl.muted = true;
      }
    }
  }, [activeVideoId, id, videoEl]);
  const playVideo = useCallback(() => {
    const video = videoEl;
    if (!video) {
      return;
    }
    if (video.paused) {
      video.play().catch(noop);
    }
  }, [videoEl]);
  const pauseAndReset = useCallback(() => {
    const video = videoEl;
    if (!video) {
      return;
    }
    video.pause();
    requestAnimationFrame(() => {
      try {
        video.currentTime = 0.001;
      } catch {}
    });
  }, [videoEl]);
  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMuted(prev => {
      const newMuted = !prev;
      const video = videoEl;
      if (video) {
        video.muted = newMuted;
        video.defaultMuted = newMuted;
        if (newMuted) {
          video.setAttribute('muted', '');
        } else {
          video.removeAttribute('muted');
          if (video.paused) {
            video.play().catch(noop);
          } else {
            try {
              video.currentTime = Math.max(0, video.currentTime - 0.000001);
            } catch {}
          }
        }
      }
      if (!newMuted) {
        onUnmute(id);
      }
      return newMuted;
    });
  }, [id, onUnmute, videoEl]);
  return <ImpressionArea onImpressionStart={playVideo} onImpressionEnd={pauseAndReset} areaThreshold={IMPRESSION_AREA_THRESHOLD} timeThreshold={IMPRESSION_TIME_THRESHOLD_MS} data-sentry-element="ImpressionArea" data-sentry-component="VideoCard" data-sentry-source-file="VideoCard.tsx">
      <Box as={motion.div} role="group" data-group="true" style={{
      position: 'relative',
      width: '100%',
      aspectRatio: '9/16',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      transform: 'translateZ(0)',
      WebkitTransform: 'translateZ(0)',
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
      contain: 'paint',
      isolation: 'isolate'
    }} onClick={onClick} onMouseEnter={() => setShowControls(true)} onMouseLeave={() => setShowControls(false)} data-sentry-element="Box" data-sentry-source-file="VideoCard.tsx">
        <video ref={setVideoEl} src={videoUrl} autoPlay loop muted playsInline preload="metadata" style={{
        width: '100%',
        height: '100%'
      }}>
          <source src={`${videoUrl}#t=0.001`} type="video/mp4" />
          <track kind="caption" />
        </video>

        <Box position="absolute" bottom="0" left="0" right="0" padding="20px 16px 16px" background="linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.6) 50%, transparent 100%)" opacity="0.95" willChange="opacity" transform="translateZ(0)" pointerEvents="none" data-sentry-element="Box" data-sentry-source-file="VideoCard.tsx">
          <Heading as="h3" color="generic.0" textStyle="paragraph.medium" fontWeight="medium" textShadow="0 2px 4px rgba(0, 0, 0, 0.3)" transition="color 0.2s ease" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" _groupHover={{
          color: 'primary.300'
        }} data-sentry-element="Heading" data-sentry-source-file="VideoCard.tsx">
            {productFocus}
          </Heading>
        </Box>

        <Box position="absolute" top="8px" right="8px" zIndex={10} data-mute-button="true" opacity={isTouchDevice || showControls ? '1' : '0'} pointerEvents={isTouchDevice || showControls ? 'auto' : 'none'} transition="opacity 0.2s ease" willChange="opacity, transform" transform="translateZ(0)" data-sentry-element="Box" data-sentry-source-file="VideoCard.tsx">
          <Button onClick={toggleMute} size="sm" variant="ghost" aria-label={isMuted ? 'Unmute' : 'Mute'} backgroundColor="rgba(0, 0, 0, 0.6)" backdropFilter={isSafari ? undefined : 'blur(8px)'} color="generic.0" borderRadius="24px" padding={2} width="48px" height="48px" display="flex" alignItems="center" justifyContent="center" _hover={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }} _active={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }} data-sentry-element="Button" data-sentry-source-file="VideoCard.tsx">
            <LucideIcon name={isMuted ? 'VolumeX' : 'Volume2'} boxSize="20px" data-sentry-element="LucideIcon" data-sentry-source-file="VideoCard.tsx" />
          </Button>
        </Box>
      </Box>
    </ImpressionArea>;
};
export default VideoCard;